import React from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/Tooltip";
import { TruncationCause } from "../types";

interface TruncationWarningProps {
  causes: TruncationCause[];
  groupByKey?: string; // names the grouped dimension in the "groups" bullet
}

function bulletFor(cause: TruncationCause, groupByKey?: string): string {
  switch (cause.kind) {
    case "criteria":
      return `More than ${cause.cap} criteria match. Only the ${cause.cap} most frequent are shown. Narrow with the Criteria filter.`;
    case "groups": {
      const dimension = groupByKey ? `“${groupByKey}” values` : "groups";
      return `More than ${cause.cap} ${dimension} match. Showing the ${cause.cap} most frequent. Narrow with a filter.`;
    }
    case "total": {
      const cap = cause.cap.toLocaleString("en-US");
      return `More than ${cap} evaluations match. Showing aggregates over the most recent ${cap}. Narrow the date range or add filters to see all.`;
    }
  }
}

/**
 * Compact "partial view" affordance: a single muted line with an info icon.
 * The per-cause detail lives in a tooltip so the notice stays small and reads
 * as information rather than an error (J1).
 */
export function TruncationWarning({ causes, groupByKey }: TruncationWarningProps) {
  if (causes.length === 0) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Data is partially shown — details"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span>Showing a partial view</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">Data is partially shown</p>
          <ul className="mt-1 list-disc list-inside space-y-1">
            {causes.map((cause) => (
              <li key={cause.kind}>{bulletFor(cause, groupByKey)}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
